import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { LogEntry } from '@/types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CameraPreview } from '@/components/Camera/CameraPreview';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useConsole } from '@/contexts/ConsoleContext';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { usePTT } from '@/contexts/PTTContext';
import { useCamera } from '@/contexts/CameraContext';
import { useElevenLabs } from '@/hooks/useElevenLabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mic, MicOff, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkSubscript } from '@/lib/remarkSubscript';

interface LogsPanelProps {
  logs: LogEntry[];
}

export function LogsPanel({ logs }: LogsPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [messageText, setMessageText] = useState('');

  const { config, updateConfig } = useAppConfig();
  const { sendMessage, agentStatus } = useWebSocket();
  const { addLog } = useConsole();
  const { startRecording, stopRecording, isRecording, isProcessing, setIsProcessing } = usePTT();
  const { captureFrame } = useCamera();
  const { transcribeAudio, playTTS, stopTTS } = useElevenLabs();

  // Auto-scroll to bottom when new log is added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [logs]);

  // Global Space key handler for PTT (moved to ManualPTTButton component)
  // This handler is no longer needed here as keyboard PTT is handled by ManualPTTButton
  const getLogTypeColor = (type: string) => {
    switch (type) {
      case 'user':
        return 'text-blue-400';
      case 'agent':
        return 'text-green-400';
      case 'system':
        return 'text-gray-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Handle sending text message
  const handleSendMessage = async () => {
    if (!messageText.trim()) return;

    const content = messageText.trim();
    const timestamp = Date.now();

    // Add user message to chat box
    addLog({
      timestamp,
      type: 'user',
      content,
    });

    // Capture camera frame if enabled
    const cameraCapture = await captureFrame();

    // Create message event
    sendMessage({
      content,
      attachments: cameraCapture ? [{
        type: 'image/base64',
        content: cameraCapture.dataUrl,
        metadata: {
          width: cameraCapture.width,
          height: cameraCapture.height,
          captureTime: cameraCapture.timestamp,
        },
      }] : undefined,
      metadata: {
        pttMode: 'manual',
        confidence: 1.0, // Text input has 100% confidence
        audioLength: 0,
      },
      timestamp,
    });

    // Clear input
    setMessageText('');
  };

  // Handle PTT recording
  const handlePTTStart = () => {
    startRecording();
  };

  const handlePTTStop = async () => {
    const audioBlob = await stopRecording();
    if (!audioBlob) return;

    try {
      // Set processing state to disable PTT
      setIsProcessing(true);

      // Transcribe audio
      const sttResult = await transcribeAudio(audioBlob);
      if (!sttResult) {
        console.warn('STT returned no result');
        return;
      }

      console.log('🎤 LogsPanel PTT: Transcribed:', sttResult.transcript);
      const transcript = sttResult.transcript.toLowerCase().trim();
      console.log('🔍 LogsPanel: Checking keywords for transcript:', transcript);

      // Log character codes for debugging Vietnamese encoding issues
      console.log('🔤 LogsPanel Character analysis:', {
        transcriptLength: transcript.length,
        firstChars: transcript.substring(0, 20).split('').map(c => `${c}(${c.charCodeAt(0)})`).join(' '),
        containsCam: transcript.indexOf('cam'),
        containsCamera: transcript.indexOf('camera'),
        containsMo: transcript.indexOf('mở'),
        containsBat: transcript.indexOf('bật'),
        containsTat: transcript.indexOf('tắt')
      });

      // Check for keyword commands
      // Camera control keywords - check if transcript contains camera-related words
      const hasCameraKeyword = transcript.includes('cam') || transcript.includes('camera');
      const hasEnableKeyword = transcript.includes('mở') || transcript.includes('bật');
      const hasDisableKeyword = transcript.includes('tắt');

      console.log('📋 LogsPanel Keyword analysis:', {
        original: sttResult.transcript,
        lowercase: transcript,
        hasCameraKeyword,
        hasEnableKeyword,
        hasDisableKeyword,
        willExecuteCameraCommand: hasCameraKeyword && (hasEnableKeyword || hasDisableKeyword)
      });

      if (hasCameraKeyword && (hasEnableKeyword || hasDisableKeyword)) {
        const shouldEnable = hasEnableKeyword;
        const action = shouldEnable ? 'bật' : 'tắt';
        const responseText = `Em đã ${action} camera theo yêu cầu ạ`;

        console.log('📷 ✅ LogsPanel Camera command DETECTED - Executing:', {
          transcript,
          shouldEnable,
          action,
          hasCameraKeyword,
          hasEnableKeyword,
          hasDisableKeyword
        });

        // Execute camera action by updating config (CameraContext will handle enable/disable)
        updateConfig({ cameraEnabled: shouldEnable });
        console.log('📷 LogsPanel: Camera config updated to:', shouldEnable);

        // Play TTS response
        await playTTS(responseText);

        // Add to chat log (but don't send to agent)
        const timestamp = Date.now();
        addLog({
          timestamp,
          type: 'system',
          content: responseText,
        });

        console.log('📷 LogsPanel Camera control:', responseText);
        return;
      }

      // Stop keywords
      const hasStopKeyword = transcript.includes('thôi') || transcript.includes('dừng');

      console.log('📋 LogsPanel Stop keyword analysis:', {
        transcript,
        hasStopKeyword,
        willExecuteStopCommand: hasStopKeyword
      });

      if (hasStopKeyword) {
        console.log('🛑 ✅ LogsPanel Stop command DETECTED - Executing:', transcript);

        // Stop any ongoing TTS
        stopTTS();

        const timestamp = Date.now();

        // If agent is busy, send stop command
        if (agentStatus === 'busy') {
          sendMessage({
            content: `\\stop ${sttResult.transcript}`,
            attachments: undefined,
            metadata: {
              pttMode: 'manual',
              confidence: sttResult.confidence,
              audioLength: audioBlob.size,
            },
            timestamp,
          });

          // Add to chat log
          addLog({
            timestamp,
            type: 'user',
            content: `\\stop ${sttResult.transcript}`,
          });

          console.log('🛑 LogsPanel Stop command sent to agent');
        } else {
          // Just add stop message to chat (no agent emit)
          addLog({
            timestamp,
            type: 'system',
            content: 'Stopped audio playback',
          });
        }

        return;
      }

      // Normal processing - No keyword command detected
      console.log('📤 LogsPanel: No keyword command detected - Sending as normal message');

      const timestamp = Date.now();

      // Add user message to chat box
      addLog({
        timestamp,
        type: 'user',
        content: sttResult.transcript,
      });

      // Capture camera frame if enabled
      const cameraCapture = await captureFrame();

      // Create message event
      sendMessage({
        content: sttResult.transcript,
        attachments: cameraCapture ? [{
          type: 'image/base64',
          content: cameraCapture.dataUrl,
          metadata: {
            width: cameraCapture.width,
            height: cameraCapture.height,
            captureTime: cameraCapture.timestamp,
          },
        }] : undefined,
        metadata: {
          pttMode: 'manual',
          confidence: sttResult.confidence,
          audioLength: audioBlob.size,
        },
        timestamp,
      });
    } catch (error) {
      console.error('Failed to process voice message:', error);
    } finally {
      // Always reset processing state
      setIsProcessing(false);
    }
  };

  // Handle Enter key
  // - Shift+Enter: newline (multi-line)
  // - Enter + text length >= 3: send message
  // - Enter + text length < 3: do nothing
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Check if message has at least 3 characters
      if (messageText.trim().length >= 3) {
        e.preventDefault();
        handleSendMessage();
      } else {
        // Prevent default to avoid newline when message is too short
        e.preventDefault();
      }
    }
    // Shift+Enter is allowed for newline (default behavior)
  };

  return (
    <div className="flex flex-col h-full">
      {/* Camera Preview */}
      <div className="mb-4">
        <CameraPreview />
      </div>

      {/* Chat Box */}
      <div className="flex-1 flex flex-col min-h-0 p-4 bg-muted/30 rounded-lg border">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="text-sm font-semibold text-foreground">Chat box</h4>
          {agentStatus === 'busy' && (
            <div className="flex items-center gap-1 text-xs text-yellow-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Agent processing...</span>
            </div>
          )}
        </div>

        {/* Logs Area */}
        <ScrollArea className="flex-1 mb-3">
          {logs.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No messages yet
            </div>
          ) : (
            <div className="space-y-2 pr-2">
              {logs.map((log, index) => {
                // Check if this is a status message (contains ⌛🔑⚡)
                const isStatusMessage = log.type === 'agent' &&
                  log.content.includes('⌛') &&
                  log.content.includes('🔑') &&
                  log.content.includes('⚡');

                return (
                  <div
                    key={log.id}
                    ref={index === logs.length - 1 ? scrollRef : null}
                    className={isStatusMessage
                      ? "text-xs py-1"
                      : "text-xs p-2 rounded bg-background border"
                    }
                  >
                    {!isStatusMessage && (
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className={`font-medium ${getLogTypeColor(log.type)}`}>
                          {log.type.toUpperCase()}:
                        </span>
                        <span className="text-muted-foreground">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                    )}
                    <div className={isStatusMessage
                      ? "text-[10px] text-muted-foreground/60 break-words prose prose-sm dark:prose-invert max-w-none"
                      : "p-2 bg-muted/50 rounded text-foreground break-words prose prose-sm dark:prose-invert max-w-none"
                    }>
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkSubscript]}>
                        {log.content}
                      </ReactMarkdown>
                    </div>
                    {log.metadata && !isStatusMessage && (
                      <div className="mt-2 p-2 bg-muted/30 rounded text-muted-foreground text-xs font-mono max-h-[4.5rem] overflow-hidden">
                        <pre className="whitespace-pre-wrap">{JSON.stringify(log.metadata, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Chat Input Section */}
        <div className="flex gap-2 items-end">
          {/* Text Input with Send Button */}
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message (min 3 chars)"
              className="min-h-[40px] max-h-[120px] resize-none pr-12 text-sm"
              rows={1}
            />
            <Button
              size="sm"
              onClick={handleSendMessage}
              disabled={messageText.trim().length < 3}
              className="absolute right-1 bottom-1 h-8 w-8 p-0"
              title="Send message (min 3 characters)"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* PTT Button - Only show if voice mode is enabled */}
          {config.voiceModeEnabled && (
            <Button
              size="lg"
              variant={isRecording ? "destructive" : "default"}
              className={cn(
                "h-[40px] w-[40px] p-0 flex-shrink-0 transition-all",
                isRecording && "animate-pulse"
              )}
              disabled={isProcessing}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!isProcessing) handlePTTStart();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                if (!isProcessing) handlePTTStop();
              }}
              onMouseLeave={() => {
                if (isRecording && !isProcessing) {
                  handlePTTStop();
                }
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                if (!isProcessing) handlePTTStart();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                if (!isProcessing) handlePTTStop();
              }}
              title={isProcessing ? "⏳ Processing STT..." : isRecording ? "🔴 Recording... (Release to send)" : "🎤 Hold to record voice message"}
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isRecording ? (
                <Mic className="w-5 h-5 scale-125" />
              ) : (
                <MicOff className="w-5 h-5" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
